import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./cn";

/**
 * Port of the reference's Card (src/components/ui/Card.tsx). Variants map
 * onto the `.ds-card*` / `.card-selectable` classes carried over verbatim
 * in globals.css (B1) — this component only selects among them.
 */
const cardVariants = cva("text-ink", {
  variants: {
    variant: {
      default: "ds-card ds-radius-card",
      interactive: "ds-card-interactive ds-radius-card card-selectable",
      selected: "ds-card ds-radius-card ds-card-selected",
      summary: "ds-card-summary",
      hero: "ds-card-hero",
      subtle: "ds-card-subtle ds-radius-card",
    },
    padding: {
      none: "",
      sm: "p-4",
      md: "p-5",
      lg: "p-6",
    },
  },
  defaultVariants: {
    variant: "default",
    padding: "md",
  },
});

interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, padding, ...props }, ref) => {
    const variantClassName = cardVariants({ variant, padding });
    // A caller supplying its own rounding utility (or ds-radius-*) wins over
    // the variant default instead of colliding with it.
    const hasExplicitRadius =
      typeof className === "string" && /\b(?:rounded|ds-radius-)/.test(className);
    const baseClassName = hasExplicitRadius
      ? variantClassName.replace(/\bds-radius-card\b/g, "")
      : variantClassName;

    return <div ref={ref} className={cn(baseClassName, className)} {...props} />;
  },
);

Card.displayName = "Card";

export { Card, cardVariants };
export type { CardProps };
